import { Box, Grid } from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import ProFlowAddressComponentV2 from '../../../../../../tarifications/src/legacy/ProFlowAddressComponentV2';
import { Change } from '../../../../../../../types/Form';
import { Enterprise } from '../../../../../../../types/ProductFactory';
import TextInput from '../input/TextInput';
import { MaskedInput } from 'wg-fe-ui';
import styled from 'styled-components';
import { string } from '../../../../../../common/src/constants/validationSchemas';
import { object } from 'yup';
import { checkIfCbeIsValid } from '../../../../../../common/src/legacy/services/apiRouterService';
import { debounce } from 'lodash';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import QuestionGroupActions from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupActions';
import QuestionTitle from '../../../../../../sales-conversation/src/components/legacy/questions/QuestionTitle';
import AdvisoryFlowMainContainer from '../../../ui/advisory-flow/components/app-shell/AdvisoryFlowMainContainer';
import QuestionGroupBody from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupBody';
import QuestionGroupAssistantQuestion from '../../../ui/advisory-flow/components/question-group-components/QuestionGroupAssistantQuestion';

interface Props {
  isMobile?: boolean;
  company: Enterprise;
  previousSubView: () => void;
  nextQuestionHandler: (values: Enterprise) => void;
  handleOnChange: (arg0: Enterprise | boolean) => void;
}

const debouncedFunction = debounce(
  async (value: string) => await checkIfCbeIsValid(value),
  300,
  { leading: true },
);

const CbeData: React.FC<React.PropsWithChildren<Props>> = ({
  company,
  previousSubView,
  nextQuestionHandler,
}) => {
  const { t } = useTranslation();

  const cbeDataValidationSchema = object().shape({
    name: string.required,
    cbe: string.required.test('VAT', t('CBE is not valid'), async (value) => {
      const result = await debouncedFunction(value);

      if (result) {
        const [, status] = result;
        if (status !== 200) return false;
        return true;
      }

      return false;
    }),
  });

  const {
    getValues,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm<Enterprise>({
    resolver: yupResolver(cbeDataValidationSchema),
    defaultValues: {
      name: company.name,
      cbe: company.company_registration?.nr || company.cbe,
      address: company.address,
    },
  });

  const handleChange = (e: Change) => {
    setValue(e.name as keyof Enterprise, e.value as keyof Enterprise);
  };

  const Question = () => (
    <QuestionGroupAssistantQuestion
      question={t('Is this info about your company correct?')}
      description={t('If not, you can edit the details that are incorrect')}
    />
  );

  return (
    <AdvisoryFlowMainContainer data-test-id="ProdFac_CbeData">
      <Question />
      <QuestionGroupBody>
        <Grid templateColumns="repeat(2, 1fr)" gap="6">
          <div>
            <QuestionTitle>{t('Company name')}</QuestionTitle>
            <TextInput
              name="name"
              value={watch('name')}
              error={errors?.name?.message}
              onChange={(event) =>
                handleChange({ value: event.currentTarget.value, name: 'name' })
              }
            />
          </div>
          <div>
            <QuestionTitle>{t('CBE number')}</QuestionTitle>
            <StyledMaskedInput
              name="cbe"
              onChange={({ name, value }: Change) => {
                handleChange({
                  name,
                  value: (value as string)?.replaceAll('.', ''),
                });
              }}
              error={errors?.cbe?.message}
              value={watch('cbe')}
              type="text"
              mask="9999.999.999"
            />
          </div>
          <Box gridColumn="span 2">
            <QuestionTitle>{t('Address')}</QuestionTitle>
            <ProFlowAddressComponentV2
              onSubmit={(address) => {
                handleChange({ name: 'address', value: address });
              }}
              error={errors.address as string}
              value={getValues('address')}
              alwaysShowSearch={true}
              updateOnChange
            />
          </Box>
        </Grid>
      </QuestionGroupBody>
      <QuestionGroupActions
        onBack={previousSubView}
        onContinue={handleSubmit(nextQuestionHandler)}
      />
    </AdvisoryFlowMainContainer>
  );
};

const StyledMaskedInput = styled(MaskedInput)`
  height: fit-content;

  > label > input {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04), 0 0 2px rgba(0, 0, 0, 0.06),
      0 0 1px rgba(0, 0, 0, 0.04);
    border-radius: 4px;
    margin-top: 0;
    height: 4.5rem;
    padding-left: 1.2rem;

    ${({ prefilled }) =>
      prefilled ? `border-color: #FFBC0F; background-color: #FFF9EA;` : ''}
  }

  > div {
    margin-bottom: 0;
  }
`;

export default CbeData;
